import { logOut } from '../../service/CognitoAuthService';

// One-shot flag that survives the full-page reload performed by clearSession.
// Read-and-cleared on the landing page to show the "session expired" message
// and auto-open the login modal. See readAndClearExpiredFlag below.
export const EXPIRED_SESSION_FLAG = 'fayven:sessionExpired';

/**
 * Single authoritative session teardown.
 *
 * This is the owning layer for logging a user out, whether triggered manually
 * (navbar / user menu) or automatically (API rejects an expired token with 401).
 * It clears all persisted auth state and sends the user back to the landing
 * page. Centralizing it means a future auth fix lands in exactly one place.
 *
 * It intentionally does NOT depend on React or the redux store so it can be
 * called from the axios interceptor as well as from components. The subsequent
 * full-page reload to '/' resets redux state anyway, so a resetUserState()
 * dispatch is not required here.
 *
 * @param {Object} [options]
 * @param {boolean} [options.expired=false] - When true, sets a one-shot flag so
 *   the landing page can show "session expired" and open the login modal.
 * @param {boolean} [options.signOutCognito=false] - When true, also calls the
 *   Cognito signOut (used by the explicit manual-logout path).
 */
export function clearSession({ expired = false, signOutCognito = false } = {}) {
  try {
    if (signOutCognito) {
      // Best effort — never let a Cognito error block the teardown.
      Promise.resolve(logOut()).catch(() => {});
    }
  } catch {
    // ignore — teardown must always proceed
  }

  window.localStorage.clear();
  window.sessionStorage.clear();

  if (expired) {
    // Set AFTER clearing storage so it survives into the reloaded landing page.
    try {
      window.sessionStorage.setItem(EXPIRED_SESSION_FLAG, '1');
    } catch {
      // sessionStorage can throw in some locked-down Safari modes; the redirect
      // still breaks the loop even without the message.
    }
  }

  window.location.href = '/';
}

/**
 * Returns true exactly once after an expired-session teardown, then clears the
 * flag so a manual refresh of the landing page does not re-show the message.
 */
export function readAndClearExpiredFlag() {
  try {
    const flagged = window.sessionStorage.getItem(EXPIRED_SESSION_FLAG) === '1';
    if (flagged) window.sessionStorage.removeItem(EXPIRED_SESSION_FLAG);
    return flagged;
  } catch {
    return false;
  }
}
